import React, { useState, useEffect } from 'react';
import { getMapData } from '@mappedin/mappedin-js';
import { useMappedInSettings } from '../../hooks/useMappedInSettings';
import { useParams } from 'react-router-dom';

// Default fallback configuration - will be replaced by dynamic settings
const DEFAULT_MAPPEDIN_OPTIONS = {
  key: "mik_gxWE7PUdHxoK9qNlr555107ac",
  secret: "mis_hvx40sGJZ2cuyzSDEHv55PtldjuszH20dVqhRkBV8R597271073",
  mapId: "69709560a8a681000bf10352",
};

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: { fontFamily: 'system-ui, sans-serif', padding: '24px', maxWidth: '1200px', margin: '0 auto', background: '#f8fafc', minHeight: '100vh' },
  header: { background: 'linear-gradient(135deg, #1e293b, #334155)', color: 'white', borderRadius: '12px', padding: '20px 24px', marginBottom: '24px' },
  title: { margin: 0, fontSize: '22px', fontWeight: 700 },
  subtitle: { margin: '4px 0 0', fontSize: '13px', opacity: 0.7 },
  btn: (color = '#6366f1', disabled = false) => ({
    background: disabled ? '#94a3b8' : color,
    color: 'white', border: 'none', borderRadius: '8px',
    padding: '10px 20px', fontSize: '14px', fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer', marginRight: '8px', marginBottom: '8px',
  }),
  card: { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' },
  cardTitle: { fontSize: '14px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' },
  badge: (color = '#6366f1') => ({
    display: 'inline-block', background: `${color}18`, color, border: `1px solid ${color}40`,
    borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 600, margin: '2px',
  }),
  row: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
  locCard: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', flex: '1 1 340px', minWidth: 0 },
  locName: { fontWeight: 700, fontSize: '14px', color: '#1e293b', marginBottom: '6px' },
  distBadge: (d) => ({
    display: 'inline-block', background: d < 50 ? '#dcfce7' : d < 150 ? '#fef9c3' : '#fee2e2',
    color: d < 50 ? '#166534' : d < 150 ? '#854d0e' : '#991b1b',
    borderRadius: '6px', padding: '2px 8px', fontSize: '12px', fontWeight: 700, marginLeft: '8px',
  }),
  input: { padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginBottom: '8px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 16px', color: '#dc2626', marginBottom: '16px', fontSize: '13px' },
  info: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px 16px', color: '#1d4ed8', marginBottom: '16px', fontSize: '13px' },
  spinner: { display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: '8px', verticalAlign: 'middle' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { background: '#f1f5f9', padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
};

export default function MappedInDataExplorer() {
  const { userId } = useParams();
  
  // Fetch MappedIn settings dynamically
  const { settings: mappedInConfig, loading: configLoading } = useMappedInSettings(userId);
  
  const [loading, setLoading] = useState(false);
  const [loadingDistances, setLoadingDistances] = useState(false);
  const [error, setError] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [distances, setDistances] = useState([]);
  const [startName, setStartName] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [activeTab, setActiveTab] = useState('locations');

  // Update start location when config loads
  useEffect(() => {
    if (mappedInConfig?.mappedInStartLocationName) {
      setStartName(mappedInConfig.mappedInStartLocationName);
    } else if (mappedInConfig?.mappedInStartLocation) {
      setStartName(mappedInConfig.mappedInStartLocation);
    } else {
      setStartName('LOC-1IP30ZO0');
    }
  }, [mappedInConfig]);

  // ── Load map data ──────────────────────────────────────────────────────────
  const loadData = async () => {
    if (!mappedInConfig?.key || !mappedInConfig?.secret || !mappedInConfig?.mapId) {
      setError('MappedIn configuration not found. Please configure in Map Settings.');
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const options = {
        key: mappedInConfig.key,
        secret: mappedInConfig.secret,
        mapId: mappedInConfig.mapId,
      };
      const data = await getMapData(options);
      setMapData(data);

      // Enterprise locations with categories + tags
      const locs = data.getByType('enterprise-location');
      const processed = locs.map(l => ({
        id: l.id,
        name: l.name || '(unnamed)',
        externalId: l.externalId || '',
        categories: (l.categories || []).map(c => c.name),
        tags: l.tags || [],
      }));
      setLocations(processed);

      // Unique categories
      const catSet = new Set();
      processed.forEach(l => l.categories.forEach(c => catSet.add(c)));
      setCategories([...catSet].sort());

      console.log('[Explorer] Loaded', processed.length, 'enterprise locations');
      processed.forEach(l => {
        console.log(l.name, '|', l.categories.join(', '), '|', l.tags.join(', '));
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Calculate distances from start ────────────────────────────────────────
  const calcDistances = async () => {
    if (!mapData) return;
    setLoadingDistances(true);
    setError(null);
    try {
      const locs = mapData.getByType('enterprise-location');

      // Find start location
      const start = locs.find(l =>
        l.name === startName || l.externalId === startName
      );
      if (!start) throw new Error(`Start location "${startName}" not found. Check the name/externalId.`);

      const results = [];
      for (const loc of locs) {
        if (loc.id === start.id) continue;
        try {
          const dirs = await mapData.getDirections(start, loc);
          results.push({
            name: loc.name || '(unnamed)',
            externalId: loc.externalId || '',
            categories: (loc.categories || []).map(c => c.name),
            distanceM: dirs ? Math.round(dirs.distance) : null,
            distanceFt: dirs ? Math.round(dirs.distance * 3.28084) : null,
          });
        } catch {
          results.push({ name: loc.name || '(unnamed)', externalId: loc.externalId || '', categories: [], distanceM: null, distanceFt: null });
        }
      }

      // Sort by distance
      results.sort((a, b) => {
        if (a.distanceM === null) return 1;
        if (b.distanceM === null) return -1;
        return a.distanceM - b.distanceM;
      });

      setDistances(results);
      console.log('[Explorer] Distances calculated:', results);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingDistances(false);
    }
  };

  // ── Filtered locations ─────────────────────────────────────────────────────
  const filtered = locations.filter(l => {
    const catOk = !filterCat || l.categories.some(c => c.toLowerCase().includes(filterCat.toLowerCase()));
    const tagOk = !filterTag || l.tags.some(t => t.toLowerCase().includes(filterTag.toLowerCase()));
    return catOk && tagOk;
  });

  const Tab = ({ id, label, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      style={{
        padding: '8px 16px', border: 'none', borderRadius: '8px', cursor: 'pointer',
        fontWeight: 600, fontSize: '13px', marginRight: '6px',
        background: activeTab === id ? '#6366f1' : '#f1f5f9',
        color: activeTab === id ? 'white' : '#64748b',
      }}
    >
      {label} {count !== undefined && <span style={{ opacity: 0.7, fontSize: '11px' }}>({count})</span>}
    </button>
  );

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={s.header}>
        <h1 style={s.title}>🗺 MappedIn Data Explorer</h1>
        <p style={s.subtitle}>Explore enterprise locations, categories, tags & distances</p>
      </div>

      {/* Controls */}
      <div style={s.card}>
        <div style={s.cardTitle}>Controls</div>
        <button style={s.btn('#6366f1', loading)} onClick={loadData} disabled={loading}>
          {loading && <span style={s.spinner} />}
          {loading ? 'Loading Map Data…' : '🔄 Load Map Data'}
        </button>
        {mapData && (
          <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 600 }}>
            ✓ Map loaded — {locations.length} enterprise locations
          </span>
        )}
      </div>

      {error && <div style={s.error}>❌ {error}</div>}

      {mapData && (
        <>
          {/* Tabs */}
          <div style={{ marginBottom: '16px' }}>
            <Tab id="locations" label="Locations" count={filtered.length} />
            <Tab id="categories" label="Categories" count={categories.length} />
            <Tab id="distances" label="Distances" count={distances.length} />
            <Tab id="raw" label="Raw JSON" />
          </div>

          {/* ── Locations Tab ── */}
          {activeTab === 'locations' && (
            <div style={s.card}>
              <div style={s.cardTitle}>Enterprise Locations — Categories & Tags</div>

              {/* Filters */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>Filter by Category</label>
                  <input style={s.input} placeholder="e.g. Food & Drinks" value={filterCat} onChange={e => setFilterCat(e.target.value)} />
                </div>
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>Filter by Tag</label>
                  <input style={s.input} placeholder="e.g. coffee" value={filterTag} onChange={e => setFilterTag(e.target.value)} />
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>#</th>
                      <th style={s.th}>Name</th>
                      <th style={s.th}>External ID</th>
                      <th style={s.th}>Categories</th>
                      <th style={s.th}>Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((l, i) => (
                      <tr key={l.id}>
                        <td style={{ ...s.td, color: '#94a3b8', fontSize: '12px' }}>{i + 1}</td>
                        <td style={{ ...s.td, fontWeight: 600 }}>{l.name}</td>
                        <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', color: '#6366f1' }}>{l.externalId || '—'}</td>
                        <td style={s.td}>
                          {l.categories.length > 0
                            ? l.categories.map(c => <span key={c} style={s.badge('#0ea5e9')}>{c}</span>)
                            : <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>}
                        </td>
                        <td style={s.td}>
                          {l.tags.length > 0
                            ? l.tags.map(t => <span key={t} style={s.badge('#10b981')}>{t}</span>)
                            : <span style={{ color: '#94a3b8', fontSize: '12px' }}>—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>No locations match the filter</div>
                )}
              </div>
            </div>
          )}

          {/* ── Categories Tab ── */}
          {activeTab === 'categories' && (
            <div style={s.card}>
              <div style={s.cardTitle}>All Categories ({categories.length})</div>
              <div style={s.row}>
                {categories.map(c => {
                  const count = locations.filter(l => l.categories.includes(c)).length;
                  return (
                    <div key={c} style={{ ...s.locCard, flex: '0 1 auto', minWidth: 'auto', cursor: 'pointer' }}
                      onClick={() => { setFilterCat(c); setActiveTab('locations'); }}>
                      <span style={s.badge('#0ea5e9')}>{c}</span>
                      <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>{count} location{count !== 1 ? 's' : ''}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Distances Tab ── */}
          {activeTab === 'distances' && (
            <div style={s.card}>
              <div style={s.cardTitle}>Distance Calculator</div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '260px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '4px' }}>
                    Start Location (name or externalId)
                  </label>
                  <input style={s.input} value={startName} onChange={e => setStartName(e.target.value)} placeholder="e.g. LOC-1IP30ZO0" />
                </div>
                <button style={s.btn('#f59e0b', loadingDistances || !mapData)} onClick={calcDistances} disabled={loadingDistances || !mapData}>
                  {loadingDistances && <span style={s.spinner} />}
                  {loadingDistances ? 'Calculating…' : '📏 Calculate Distances'}
                </button>
              </div>

              {distances.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>#</th>
                        <th style={s.th}>Location</th>
                        <th style={s.th}>External ID</th>
                        <th style={s.th}>Categories</th>
                        <th style={s.th}>Distance (m)</th>
                        <th style={s.th}>Distance (ft)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distances.map((d, i) => (
                        <tr key={i}>
                          <td style={{ ...s.td, color: '#94a3b8', fontSize: '12px' }}>{i + 1}</td>
                          <td style={{ ...s.td, fontWeight: 600 }}>{d.name}</td>
                          <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '12px', color: '#6366f1' }}>{d.externalId || '—'}</td>
                          <td style={s.td}>
                            {d.categories.map(c => <span key={c} style={s.badge('#0ea5e9')}>{c}</span>)}
                          </td>
                          <td style={s.td}>
                            {d.distanceM !== null
                              ? <span style={s.distBadge(d.distanceM)}>{d.distanceM} m</span>
                              : <span style={{ color: '#94a3b8' }}>N/A</span>}
                          </td>
                          <td style={s.td}>
                            {d.distanceFt !== null
                              ? <span style={{ fontSize: '13px', color: '#64748b' }}>{d.distanceFt} ft</span>
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {distances.length === 0 && !loadingDistances && (
                <div style={s.info}>Enter a start location name or externalId and click "Calculate Distances" to see distances to all other locations.</div>
              )}
            </div>
          )}

          {/* ── Raw JSON Tab ── */}
          {activeTab === 'raw' && (
            <div style={s.card}>
              <div style={s.cardTitle}>Raw Location Data (JSON)</div>
              <pre style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: '8px', padding: '16px', overflowX: 'auto', fontSize: '12px', maxHeight: '600px', overflowY: 'auto' }}>
                {JSON.stringify(locations, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}

      {!mapData && !loading && (
        <div style={s.info}>Click "Load Map Data" to fetch enterprise locations, categories, tags, and calculate distances.</div>
      )}
    </div>
  );
}
